// Chronological activity feed for /tickets/feed.
//
// Merges two streams into a single time-sorted list:
//   1. public.agent_activity_log — agent ship/pick/block/ask-user events
//   2. public.tickets — most recently updated tickets (any assignee)
//
// Returns flat array of feed entries shaped as:
//   {
//     id,                  // stable key for DOMQL keyed render
//     kind,                // 'shipped' | 'picked_up' | 'blocked' | ...
//                          //   | 'ticket_update'   (fallback kind from tickets table)
//     tsISO,               // event time (agent_activity_log.created_at or tickets.updated_at)
//     actorEmail,          // agent_name if activity log; assignee_email otherwise
//     ticketId,            // when present
//     ticketExternalId,
//     ticketTitle,
//     ticketType,
//     ticketState,
//     commitSha,
//     note
//   }

import { SUPABASE_URL, restHeaders } from '../../../shared/functions/supabaseConfig.js'

const WORKSPACE_ID = '69e5911201b0ef47b675463f'

export const fetchActivityFeed = async ({ limit = 50 } = {}) => {
  const [logRes, ticketsRes] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/agent_activity_log?select=id,agent_name,ticket_id,kind,commit_sha,note,metadata,created_at,agent_id&workspace_id=eq.${WORKSPACE_ID}&order=created_at.desc&limit=${limit}`,
      { headers: restHeaders() }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/tickets?select=id,external_id,type,title,state,assignee_email,updated_at,metadata&workspace_id=eq.${WORKSPACE_ID}&deleted=eq.false&order=updated_at.desc&limit=${limit}`,
      { headers: restHeaders() }
    )
  ])

  const logs = logRes.ok ? await logRes.json() : []
  const tickets = ticketsRes.ok ? await ticketsRes.json() : []

  // Build ticket lookup so we can enrich activity log rows.
  const tById = new Map()
  for (const t of tickets) tById.set(t.id, t)

  const feed = []

  for (const l of logs) {
    const t = l.ticket_id ? tById.get(l.ticket_id) : null
    feed.push({
      id: `log:${l.id}`,
      kind: l.kind,
      tsISO: l.created_at,
      actorEmail: l.agent_name ? `${l.agent_name.toLowerCase()}-agent@symbols.app` : null,
      actorDisplay: l.agent_name || 'agent',
      ticketId: l.ticket_id,
      ticketExternalId: t?.external_id,
      ticketTitle: t?.title,
      ticketType: t?.type,
      ticketState: t?.state,
      commitSha: l.commit_sha,
      note: l.note
    })
  }

  // Fold in ticket updates that don't have a matching activity log entry
  // (human edits, external syncs, etc.) so humans aren't invisible.
  const loggedTicketIds = new Set(logs.filter(l => l.ticket_id).map(l => l.ticket_id))
  for (const t of tickets) {
    if (loggedTicketIds.has(t.id)) continue
    feed.push({
      id: `ticket:${t.id}`,
      kind: 'ticket_update',
      tsISO: t.updated_at,
      actorEmail: t.assignee_email,
      actorDisplay: t.assignee_email?.split('@')[0] || 'someone',
      ticketId: t.id,
      ticketExternalId: t.external_id,
      ticketTitle: t.title,
      ticketType: t.type,
      ticketState: t.state,
      commitSha: null,
      note: null
    })
  }

  feed.sort((a, b) => new Date(b.tsISO || 0) - new Date(a.tsISO || 0))
  return feed.slice(0, limit)
}
